/**
 * Plan gating for the client (pricing v2).
 *
 * The server works out what an account may do (functions/lib/entitlements.js)
 * and v8UserAuthV4 returns it as authState.entitlements. Gate features with
 * can("feature") instead of comparing authState.plan, so Free, legacy and v2
 * plans all follow the same rules. The server enforces the same rules.
 *
 * The offline-license build has no entitlements; legacyEntitlements() rebuilds
 * them from its plan so it keeps working as before.
 */

import { authState } from "@app/store/authStore.svelte.js";

const ALL = [
    "basicExport", "burnIn", "broadcastExport", "aiAssistant",
    "aiTranscription", "batch", "translation", "shotDescriptions",
    "embed608", "extract608", "teams", "iconik", "workOrderApi", "liveCaptions", "advancedAi",
    "agentPurchasable"
];
const STARTER = ["basicExport", "burnIn", "aiAssistant", "broadcastExport", "aiTranscription", "agentPurchasable"];

/** Subtitle file extensions every plan exports without a watermark (Free included). */
export const BASIC_SUBTITLE_EXTS = ["srt", "vtt", "webvtt", "txt"];

/** Export kinds that are never watermarked: basic subtitle files and burn-in video. */
const BASIC_EXPORTS = new Set([...BASIC_SUBTITLE_EXTS, "burnIn"]);

function featureSet(enabled) {
    const features = {};
    for (const name of ALL) features[name] = enabled.includes(name);
    return features;
}

/** Entitlements for a legacy plan name (offline licenses, and sign-in before V4 data arrives). */
export function legacyEntitlements(plan, ad) {
    if (!plan) return null;
    const features = featureSet(plan === "starter" ? STARTER : ALL);
    features.audioDescription = Boolean(ad);
    features.adTeamWide = false;
    features.agent = false;
    return { tier: plan, planVersion: 1, legacy: true, trial: false, features, seats: null, limits: null };
}

/* Lifetime licences: desktop editing and exports only, none of the paid cloud features (AI, transcription,
   translation, live captions, teams). Pro adds 608/708 embed and extract, which run locally (cc-embed). */
const LIFETIME = {
    starter: ["basicExport", "burnIn", "broadcastExport"],
    pro: ["basicExport", "burnIn", "broadcastExport", "embed608", "extract608"],
};

/** Entitlements for a lifetime licence (single user, no AD). */
export function lifetimeEntitlements(tier) {
    const features = featureSet(LIFETIME[tier] || LIFETIME.starter);
    features.audioDescription = false;
    features.adTeamWide = false;
    features.agent = false;
    return { tier, planVersion: 2, legacy: false, lifetime: true, trial: false, features, seats: { included: 1, extra: 0, used: 1, limit: 1 }, limits: null };
}

export function entitlements() {
    return authState.entitlements || legacyEntitlements(authState.plan, authState.ad);
}

/** Whether the account's plan has arrived (false while the sign-in check is still running). */
export function planLoaded() {
    return entitlements() !== null;
}

/** Whether the signed-in account's plan includes a feature. */
export function can(feature) {
    const current = entitlements();
    return Boolean(current && current.features && current.features[feature]);
}

/** Whether the plan includes the AI Agent: every Pro and Enterprise subscription, legacy included (not lifetime). */
export function hasAgentPlan() {
    const current = entitlements();
    return Boolean(current && !current.lifetime && (current.tier === "pro" || current.tier === "enterprise"));
}

export function isFreePlan() {
    return entitlements()?.tier === "free";
}

export function isTrial() {
    return authState.status === "in_trial";
}

/**
 * Whether an export gets the "TRIAL VERSION" text. Basic subtitle files and
 * burn-in video are always clean; everything else is watermarked during a
 * trial and on the Free plan.
 * @param {string} kind  a subtitle file extension (e.g. "scc"), or an export kind:
 *   "burnIn", "alpha", "image", "transcript", "dialogueList", "ccsl", "embed",
 *   "workOrder", "aiTranscript"
 */
export function shouldWatermark(kind) {
    if (BASIC_EXPORTS.has(String(kind || "").toLowerCase())) return false;
    return isTrial() || isFreePlan();
}

/** Subtitle extensions the plan may export (Free: basic formats only). */
export function allowedSubtitleExts(exts) {
    if (can("broadcastExport")) return exts;
    return exts.filter((ext) => BASIC_SUBTITLE_EXTS.includes(ext));
}

/** The authState fields older code reads (plan, status, ad, type), built from V4 entitlements. */
export function authFieldsFromEntitlements(ent, subscriptionList) {
    const unlimited = (subscriptionList || []).some((item) => /unlimited/.test(item?.subscription?.plan_id || ""));
    // Same meaning as the server's statusMapping: anything not paid-up behaves like a trial.
    const status = ent.status === "active" ? "active" : "in_trial";
    return {
        plan: ent.tier,
        type: unlimited ? "unlimited" : "default",
        status,
        ad: Boolean(ent.features.audioDescription),
        entitlements: ent
    };
}
